import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import React, { useCallback, useContext, useMemo } from 'react';

import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import { useOperationsLogs } from '@/hooks/useRewardCards';
import PersonContext from '@/pages/Person/PersonContext';
import RewardCardRepository from '@/repositories/RewardCardRepository';
import Alert from '@/ui/components/Alert';
import Button from '@/ui/components/Button';
import DataList, { DataRow } from '@/ui/components/DataList';
import DualTime from '@/ui/components/DualTime';
import Heading from '@/ui/components/Heading';
import LoadingSkeleton from '@/ui/components/LoadingSkeleton';
import { dateFromDateStringWithAppendedTime } from '@/utils/dates';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';
import snakeCaseToTitleCase from '@/utils/snakeToTitleCase';

const OperationsLogsList = () => {
  const { addMessage } = useContext(AppContext);
  const { person } = useContext(PersonContext);
  const rewardCardRepository = RewardCardRepository();
  const operationsLogsParams = useMemo(() => ({ is_completed: false }), []);
  const { data: operationsLogsResponse, isPending: isPendingOperationsLogs } = useOperationsLogs({
    personId: person.id,
    params: operationsLogsParams
  });
  const { mutate } = useMutation({
    mutationFn: (operationsLogId: string) => {
      return rewardCardRepository.retryFailedOperation({ operationsLogId });
    },
    onSuccess: () => {
      addMessage('success', 'Operation retried successfully');
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const handleClick = useCallback(
    (operationsLogId: string) => {
      return confirm('Are you sure you want to retry this operation?')
        ? mutate(operationsLogId)
        : null;
    },
    [mutate]
  );

  return (
    <div>
      {isPendingOperationsLogs ? (
        <LoadingSkeleton spinnerProps={{ label: 'Loading operations logs...' }} />
      ) : operationsLogsResponse?.results.length ? (
        <>
          {operationsLogsResponse.results.map((operationsLog) => {
            return (
              <div key={operationsLog.id} className="my-2">
                <Alert level={operationsLog.errors ? 'error' : 'warn'}>
                  <Heading
                    value={4}
                    text={`Operation in progress - ${snakeCaseToTitleCase(operationsLog.operation_type)}`}
                  />

                  {operationsLog.errors && (
                    <DataList>
                      <DataRow label="Errors">
                        {operationsLog.errors[operationsLog.attempts] ?? (
                          <>
                            <p>
                              No error keyed by attempt number found. Rendering all errors instead.
                            </p>
                            {Object.entries(operationsLog.errors).map(([attempt, error]) => (
                              <div key={attempt}>
                                {`Attempt: ${attempt}`} - {error}
                              </div>
                            ))}
                          </>
                        )}
                      </DataRow>
                      <DataRow label="Attempts">{operationsLog.attempts}</DataRow>
                      {operationsLog.last_attempt_at && (
                        <DataRow label="Last Attempt">
                          <DualTime
                            dateTime={dateFromDateStringWithAppendedTime(
                              operationsLog.last_attempt_at
                            )}
                          />
                        </DataRow>
                      )}
                    </DataList>
                  )}
                  {!operationsLog.manual_retry_allowed && operationsLog.errors && (
                    <div className="mt-2">
                      <p className="italic">Maximum number of retries reached.</p>
                      <p className="italic">Please create a ticket mentioning this id:</p>
                      <p className="font-semibold">{operationsLog.id}</p>
                    </div>
                  )}
                  {operationsLog.errors && (
                    <div className="flex justify-end">
                      <Button
                        disabled={!operationsLog.manual_retry_allowed}
                        color={Color.BLUE}
                        size="xs"
                        onClick={() => handleClick(operationsLog.id)}>
                        Retry
                      </Button>
                    </div>
                  )}
                </Alert>
              </div>
            );
          })}
        </>
      ) : null}
    </div>
  );
};

export default OperationsLogsList;
